/**
 * App-wide visual theme.
 *
 * Each theme only remaps the NEUTRAL surface tokens (background/card/panel/…)
 * via a `[data-theme="…"]` block in `index.css`. The brand accent (`--primary`,
 * cyan-blue) is intentionally NOT overridden, so it stays consistent across
 * every theme. `default` sets no attribute and uses the base `:root`.
 *
 * Ported (and adapted to keep the blue accent) from the WebFrame fork's
 * multi-theme system.
 */
export type AppThemeName = 'default' | 'classic-dark' | 'light-gray'

export const APP_THEMES: readonly {
  value: AppThemeName
  /** i18n key; a plain fallback is passed at call sites so no locale edits are required. */
  labelKey: string
  fallbackLabel: string
}[] = [
  { value: 'default', labelKey: 'settings.general.theme.default', fallbackLabel: 'Dark (default)' },
  {
    value: 'classic-dark',
    labelKey: 'settings.general.theme.classicDark',
    fallbackLabel: 'Classic dark',
  },
  {
    value: 'light-gray',
    labelKey: 'settings.general.theme.lightGray',
    fallbackLabel: 'Light gray',
  },
] as const

export const DEFAULT_APP_THEME: AppThemeName = 'default'

export function normalizeAppTheme(value: unknown): AppThemeName {
  return APP_THEMES.some((t) => t.value === value) ? (value as AppThemeName) : DEFAULT_APP_THEME
}

/** Apply the theme to the document root; `default` clears the attribute. */
export function applyAppTheme(theme: AppThemeName): void {
  const root = document.documentElement
  if (theme === 'default') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}
